import React, { useState, useEffect, useMemo } from "react";
import { Plus, Eye, Edit, Trash2, PlayCircle, History, Layers } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import TechnicalFileForm from "./TechnicalFileForm";
import BatchProductionModal from "./BatchProductionModal";
import RecipeVersionHistoryModal from "./RecipeVersionHistoryModal";
import Button from "../Common/Button";
import { SearchInput, ListLoader, ListEmpty, ListCard, RowActions, thClass } from "../../stock/StockUI";
import { formatCurrency, SortTh, tdClass, ErrorLine, useCreateRequest } from "./technicalFilesUI";

const SubRecipesList = ({ isDarkMode, createRequestKey = 0 }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [historyFile, setHistoryFile] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [formMode, setFormMode] = useState('create');
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
    const queryClient = useQueryClient();

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchSubRecipes = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            let url = '/recipes?is_sub_recipe=true';

            if (debouncedSearchTerm) {
                url += `&search=${encodeURIComponent(debouncedSearchTerm)}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.detail || "Erreur lors de la récupération des sous-recettes");
        }
    };

    const { data: recipesData, isLoading, error } = useQuery({
        queryKey: ['sub-recipes', debouncedSearchTerm],
        queryFn: fetchSubRecipes,
        keepPreviousData: true
    });

    const deleteMutation = useMutation({
        mutationFn: async (id) => {
            const accountId = await secureStorage.getItem("selectedAccount");
            return api.delete(`/recipes/${id}`, {
                headers: { 'X-account-id': accountId.id }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['sub-recipes']);
        }
    });

    const sortedRecipes = useMemo(() => {
        if (!recipesData?.data) return [];
        const sortable = [...recipesData.data];
        sortable.sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
            if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sortable;
    }, [recipesData, sortConfig]);

    const requestSort = (key) => {
        setSortConfig({
            key,
            direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
        });
    };

    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette sous-recette ?')) {
            try {
                await deleteMutation.mutateAsync(id);
            } catch (error) {
                alert('Erreur lors de la suppression');
            }
        }
    };

    const openForm = (mode, recipe = null) => {
        setFormMode(mode);
        setSelectedRecipe(recipe);
        setShowForm(true);
    };

    useCreateRequest(createRequestKey, () => openForm('create'));

    const rowActions = (recipe) => [
        { label: 'Produire un lot', icon: PlayCircle, onClick: () => { setSelectedRecipe(recipe); setShowBatchModal(true); } },
        { label: 'Voir', icon: Eye, onClick: () => openForm('view', recipe) },
        { label: 'Modifier', icon: Edit, onClick: () => openForm('edit', recipe) },
        { label: 'Historique des versions', icon: History, onClick: () => setHistoryFile(recipe) },
        { divider: true },
        { label: 'Supprimer', icon: Trash2, danger: true, onClick: () => handleDelete(recipe.id) },
    ];

    return (
        <div className="text-sm">
            <div className="flex flex-col sm:flex-row">
                <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher une sous-recette..." />
            </div>

            {isLoading ? (
                <ListLoader label="Chargement des sous-recettes..." />
            ) : error ? (
                <ErrorLine message={error.message} />
            ) : (
                <ListCard>
                    {sortedRecipes.length === 0 ? (
                        <ListEmpty
                            icon={Layers}
                            title={debouncedSearchTerm ? 'Aucune sous-recette trouvée' : 'Aucune sous-recette'}
                            text={debouncedSearchTerm ? "Aucune sous-recette ne correspond à votre recherche." : "Aucune sous-recette disponible."}
                            action={!debouncedSearchTerm && (
                                <Button variant="tinted" size="sm" icon={Plus} className="mt-4" onClick={() => openForm('create')}>
                                    Nouvelle sous-recette
                                </Button>
                            )}
                        />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <SortTh label="Nom" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortTh label="Coût" sortKey="cost" sortConfig={sortConfig} onSort={requestSort} align="right" />
                                    <th className={thClass}><span className="sr-only">Actions</span></th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {sortedRecipes.map((recipe) => (
                                    <tr key={recipe.id} onClick={() => openForm('view', recipe)}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer">
                                        <td className={`${tdClass} font-semibold text-gray-900 dark:text-gray-100`}>{recipe.name}</td>
                                        <td className={`${tdClass} text-right font-semibold tabular-nums text-gray-900 dark:text-gray-100`}>
                                            {formatCurrency(recipe.cost)} DH
                                        </td>
                                        <td className={`${tdClass} text-right`} onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-end gap-1">
                                                <Button variant="tinted" size="sm" icon={PlayCircle}
                                                        onClick={() => { setSelectedRecipe(recipe); setShowBatchModal(true); }}>
                                                    Produire
                                                </Button>
                                                <RowActions actions={rowActions(recipe)} />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ListCard>
            )}

            {showForm && (
                <TechnicalFileForm
                    mode={formMode}
                    file={selectedRecipe}
                    isSubRecipe={true}
                    onClose={() => {
                        setShowForm(false);
                        setSelectedRecipe(null);
                    }}
                    isDarkMode={isDarkMode}
                />
            )}

            {showBatchModal && (
                <BatchProductionModal
                    recipe={selectedRecipe}
                    onClose={() => {
                        setShowBatchModal(false);
                        setSelectedRecipe(null);
                    }}
                    isDarkMode={isDarkMode}
                />
            )}

            {historyFile && (
                <RecipeVersionHistoryModal
                    recipeId={historyFile.id}
                    recipeName={historyFile.name}
                    isDarkMode={isDarkMode}
                    onClose={() => setHistoryFile(null)}
                />
            )}
        </div>
    );
};

export default SubRecipesList;
